/**
 * =============================================================================
 * FALCON - Module Deneigement des Parkings Avion
 * =============================================================================
 *
 * @file        IncidentReportModal.tsx
 * @description Modal de signalement d'incident pour les agents terrain
 *              Permet de reporter pannes, accidents, obstacles, etc.
 *
 * @version     2.2
 * @author      Equipe FALCON
 * =============================================================================
 */

import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { clsx } from 'clsx';
import {
  XMarkIcon,
  ExclamationTriangleIcon,
  WrenchScrewdriverIcon,
  TruckIcon,
  CloudIcon,
  NoSymbolIcon,
  QuestionMarkCircleIcon,
  PhoneIcon,
  PaperAirplaneIcon,
} from '@heroicons/react/24/outline';
import apiClient from '@/services/api';

interface IncidentReportModalProps {
  deneignementId: number;
  onClose: () => void;
  onSubmit: () => void;
}

type IncidentType = 'panne' | 'accident' | 'meteo' | 'obstacle' | 'autre';

const INCIDENT_TYPES: { value: IncidentType; label: string; icon: React.ElementType; color: string }[] = [
  { value: 'panne', label: 'Panne materielle', icon: WrenchScrewdriverIcon, color: 'bg-red-100 text-red-700 border-red-200' },
  { value: 'accident', label: 'Accident', icon: ExclamationTriangleIcon, color: 'bg-orange-100 text-orange-700 border-orange-200' },
  { value: 'meteo', label: 'Meteo degradee', icon: CloudIcon, color: 'bg-blue-100 text-blue-700 border-blue-200' },
  { value: 'obstacle', label: 'Obstacle', icon: NoSymbolIcon, color: 'bg-yellow-100 text-yellow-700 border-yellow-200' },
  { value: 'autre', label: 'Autre', icon: QuestionMarkCircleIcon, color: 'bg-gray-100 text-gray-700 border-gray-200' },
];

/**
 * Modal de signalement d'incident
 * Interface optimisee pour utilisation terrain (gros boutons tactiles)
 */
export function IncidentReportModal({ deneignementId, onClose, onSubmit }: IncidentReportModalProps) {
  const [type, setType] = useState<IncidentType | null>(null);
  const [description, setDescription] = useState('');

  // Mutation pour envoyer l'incident
  const submitMutation = useMutation({
    mutationFn: async () => {
      const response = await apiClient.post(`/deneignements/${deneignementId}/incidents`, {
        type,
        description,
      });
      return response.data;
    },
    onSuccess: () => {
      onSubmit();
    },
  });

  const handleSubmit = () => {
    if (!type) {
      alert('Veuillez selectionner un type d\'incident');
      return;
    }
    if (!description.trim()) {
      alert('Veuillez decrire l\'incident');
      return;
    }
    submitMutation.mutate();
  };

  const handleCallGID = () => {
    // Numero de telephone du GID (a configurer)
    window.location.href = 'tel:+33123456789';
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
      {/* Overlay */}
      <div
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative w-full sm:max-w-lg bg-white rounded-t-2xl sm:rounded-2xl shadow-xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-orange-50">
          <div className="flex items-center gap-2">
            <ExclamationTriangleIcon className="w-6 h-6 text-orange-600" />
            <h2 className="text-lg font-bold text-gray-900">Signaler un incident</h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-orange-100 active:bg-orange-200"
          >
            <XMarkIcon className="w-6 h-6 text-gray-600" />
          </button>
        </div>

        {/* Contenu */}
        <div className="p-4 overflow-y-auto max-h-[60vh]">
          {/* Selection du type */}
          <div className="mb-4">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Type d'incident
            </label>
            <div className="grid grid-cols-2 gap-2">
              {INCIDENT_TYPES.map((incidentType) => {
                const Icon = incidentType.icon;
                const isSelected = type === incidentType.value;
                return (
                  <button
                    key={incidentType.value}
                    onClick={() => setType(incidentType.value)}
                    className={clsx(
                      'flex items-center gap-2 p-3 rounded-xl border-2 transition-all',
                      isSelected
                        ? `${incidentType.color} border-current ring-2 ring-offset-1`
                        : 'bg-white border-gray-200 hover:border-gray-300'
                    )}
                  >
                    <Icon className="w-6 h-6" />
                    <span className="text-sm font-medium">{incidentType.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Description */}
          <div className="mb-4">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Description
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Decrivez l'incident..."
              className="w-full px-4 py-3 border border-gray-300 rounded-xl text-base focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              rows={4}
            />
          </div>

          {/* Message d'erreur */}
          {submitMutation.isError && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
              Erreur lors de l'envoi. Veuillez reessayer.
            </div>
          )}
        </div>

        {/* Footer avec actions */}
        <div className="p-4 border-t border-gray-200 bg-gray-50 space-y-3">
          {/* Bouton appeler GID */}
          <button
            onClick={handleCallGID}
            className="w-full flex items-center justify-center gap-2 py-3 px-4 rounded-xl border-2 border-primary-500 text-primary-700 font-semibold hover:bg-primary-50 active:bg-primary-100"
          >
            <PhoneIcon className="w-5 h-5" />
            <span>Appeler le GID</span>
          </button>

          {/* Bouton envoyer */}
          <button
            onClick={handleSubmit}
            disabled={submitMutation.isPending || !type || !description.trim()}
            className={clsx(
              'w-full flex items-center justify-center gap-2 py-4 px-6 rounded-xl text-lg font-bold text-white transition-all',
              'bg-orange-500 hover:bg-orange-600 active:bg-orange-700',
              'disabled:opacity-50 disabled:cursor-not-allowed',
              'shadow-lg shadow-orange-500/30'
            )}
          >
            {submitMutation.isPending ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
                <span>Envoi...</span>
              </>
            ) : (
              <>
                <PaperAirplaneIcon className="w-5 h-5" />
                <span>Envoyer le signalement</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

export default IncidentReportModal;
